import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs';

import { TenantState } from '@core/models/tenant.state';

/**
 * Ambient tenant scope (HLD §4 SM-02). `marketplace` scope shows every
 * participating tenant; a tenant scope filters catalog queries and swaps the
 * shell branding. In this CSR phase the storefront resolver enters/exits the
 * scope from the `/retailer/:tenantSlug` route; an SSR host resolver later
 * calls the same API at bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class TenantScopeService {
  private readonly _tenant = signal<TenantState | null>(null);

  /** The active storefront tenant, or null in marketplace scope. */
  readonly tenant = this._tenant.asReadonly();
  readonly isTenantScope = computed(() => this._tenant() !== null);

  constructor() {
    // Auto-exit: any navigation that leaves `/retailer/…` (logo, back-band,
    // cart, favorites, …) drops the storefront scope; the storefront
    // resolver re-enters it on the next `/retailer/:tenantSlug` visit.
    inject(Router)
      .events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => {
        if (!event.urlAfterRedirects.startsWith('/retailer/')) this.exitTenant();
      });
  }

  enterTenant(tenant: TenantState): void {
    this._tenant.set(tenant);
  }

  exitTenant(): void {
    this._tenant.set(null);
  }
}
